import { ApiError, apiFetch } from './apiFetch.js';

/**
 * Credentialed file downloads for api-host documents (PDF / CSV / print).
 *
 * WHY THIS EXISTS: the SPA holds the access token IN MEMORY (threat T-20-09) and
 * only `apiFetch` attaches it as `Authorization: Bearer …`. A plain `<a href>` /
 * `window.open` to the api host is a NAVIGATION — the browser cannot attach the
 * in-memory bearer token, and the api host does NOT accept the session cookie for
 * resource reads, so every such download 401'd. The fix: fetch the document
 * THROUGH apiFetch (bearer + single-flight refresh), then hand the browser a blob.
 */

/** An api-host absolute URL → the api-relative path apiFetch expects (it re-prepends the api base). */
function toApiPath(absoluteApiUrl: string): string {
  try {
    const u = new URL(absoluteApiUrl);
    return u.pathname + u.search;
  } catch {
    return absoluteApiUrl; // already relative
  }
}

async function fetchBlob(absoluteApiUrl: string): Promise<Blob> {
  const res = await apiFetch(toApiPath(absoluteApiUrl));
  if (!res.ok) throw new ApiError(res.status, `download_failed_${res.status}`);
  return res.blob();
}

/** Fetch a credentialed api-host file and trigger a browser download. */
export async function downloadCredentialed(absoluteApiUrl: string, filename: string): Promise<void> {
  const blob = await fetchBlob(absoluteApiUrl);
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = filename;
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  // Revoke after a tick so the browser has read the blob for the download.
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 10_000);
}

/** Fetch a credentialed api-host document (e.g. Reports "Print View") and open it in a new tab. */
export async function openCredentialed(absoluteApiUrl: string): Promise<void> {
  const blob = await fetchBlob(absoluteApiUrl);
  const objectUrl = URL.createObjectURL(blob);
  window.open(objectUrl, '_blank', 'noopener,noreferrer');
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
}
